import _cloneDeep from 'lodash/cloneDeep';
import { v4 as uuid } from 'uuid';
import { DRAG_ATTRIBUTE } from 'volta/components/bv-dnd';
import { IDashboardWidgetWithComponent, TDashboardColumn } from 'volta/models/types/user';
import { indexBy } from 'volta/utils/array-utils';

import { action } from '@ember/object';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

const setWidgetsColumn = (widgets: IDashboardWidgetWithComponent[], column: TDashboardColumn) => {
  return widgets.map((w) => ({ ...w, column }));
};

interface IArgs {
  widgets: Record<TDashboardColumn, IDashboardWidgetWithComponent[]>;
  onSave?: (widgets: Record<TDashboardColumn, IDashboardWidgetWithComponent[]>) => void;
}

export default class WidgetsEditBlock extends Component<IArgs> {
  @tracked display?: TDashboardColumn = this.setDisplay();

  @action
  accepts(el: HTMLElement, target: HTMLElement) {
    if (this.display === 'primary') {
      return true;
    }

    const { primary, secondary } = this.args.widgets;
    const allById = indexBy([...primary, ...secondary], '_id');
    const id = el.getAttribute(DRAG_ATTRIBUTE);
    const display = target.getAttribute('data-name') as TDashboardColumn | null;

    if (id && display) {
      return allById[id].columnsDisplay.includes(display);
    }
    return true;
  }

  get widgets() {
    return this.args.widgets;
  }

  @action
  updateDisplay(display: TDashboardColumn) {
    if (display === this.display) {
      return;
    }
    this.display = display;
    const { widgets } = this.args;
    if (widgets.secondary.length && display === 'primary') {
      const sortedWidgets = {
        primary: [
          ...widgets.primary,
          ...widgets.secondary.map((widget) => ({
            ...widget,
            column: 'primary' as TDashboardColumn
          }))
        ],
        secondary: []
      };
      this.args.onSave?.(sortedWidgets);
    } else if (display === 'secondary') {
      const sortedWidgets = widgets.primary.reduce(
        (acc: Record<TDashboardColumn, IDashboardWidgetWithComponent[]>, widget) => {
          if (!widget.columnsDisplay?.includes('primary')) {
            widget.column = 'secondary';
            acc.secondary.push(widget);
          } else {
            acc.primary.push(widget);
          }
          return acc;
        },
        { primary: [], secondary: [] }
      );
      this.args.onSave?.(sortedWidgets);
    } else {
      this.args.onSave?.(widgets);
    }
  }

  @action
  onWidgetChange(widget: IDashboardWidgetWithComponent, column: TDashboardColumn) {
    const { widgets } = this.args;
    widgets[column] = widgets[column].reduce((acc: IDashboardWidgetWithComponent[], w) => {
      if (widget._id === w._id) {
        acc.push(widget);
      } else {
        acc.push(w);
      }
      return acc;
    }, []);
    this.args.onSave?.(widgets);
  }

  @action
  onWidgetRemove(widget: IDashboardWidgetWithComponent, column: TDashboardColumn) {
    const { widgets } = this.args;

    widgets[column] = widgets[column].filter((w) => widget._id !== w._id);
    this.args.onSave?.(widgets);
  }

  @action
  onAddWidgets(widget: IDashboardWidgetWithComponent) {
    const widgets = _cloneDeep(this.args.widgets);
    widget._id = uuid();

    widgets[widget.column].push(_cloneDeep(widget));

    this.args.onSave?.(widgets);
  }

  @action
  updateWidgets(columns: Array<IDashboardWidgetWithComponent[]>) {
    const [primary = [], secondary = []] = columns;
    this.args.onSave?.({
      primary: setWidgetsColumn(primary, 'primary'),
      secondary: setWidgetsColumn(secondary, 'secondary')
    });
  }

  setDisplay() {
    const { widgets } = this.args;
    return widgets.secondary.length ? 'secondary' : 'primary';
  }
}
